import { useState, useMemo } from 'react'

import PropTypes from 'prop-types'
import Link from 'next/link'
import { FormattedMessage, useIntl } from 'react-intl'
import { debounce, escapeRegExp } from 'lodash'

import { Headline, Heading } from 'components/page'
import { TagSet, Tag } from 'components/tag'
import { Muted } from 'components/text'
import { Search } from 'components/form'
import { StructuredText, SliceZone } from 'prismic/components/base'
import SliceComponents from 'prismic/components/slice/rework'
import { Prismic404 } from 'prismic/layouts/Prismic404'

import styles from './Glossary.module.css'

const DEBOUNCE_TIME = 500

export function Glossary({ layout, definitions, sections }) {
    const intl = useIntl()

    const [term, setTerm] = useState('')
    const handleTermChange = useMemo(() => debounce(setTerm, DEBOUNCE_TIME), [])

    const placeholder = intl.formatMessage({
        defaultMessage: 'Search for a definition',
        description: 'Layout glossary/layouts',
    })

    if (!layout) {
        return <Prismic404 />
    }

    return (
        <>
            <Headline>
                <StructuredText value={layout.data.headline} />
            </Headline>
            <Search onChange={handleTermChange} value={term} placeholder={placeholder} />
            <GlossaryContent term={term} sections={sections} definitions={definitions} />
        </>
    )
}

Definition.propTypes = {
    document: PropTypes.object.isRequired,
}

export function Definition({ document }) {
    return <DefinitionLayout linkToExternal {...document} />
}

// Util layouts
DefinitionLayout.propTypes = {
    linkToExternal: PropTypes.bool,
    id: PropTypes.string.isRequired,
    uid: PropTypes.string.isRequired,
    tags: PropTypes.array.isRequired,
    data: PropTypes.object.isRequired,
}

function DefinitionLayout({ uid, tags, data, linkToExternal }) {
    return (
        <>
            <Heading hash={linkToExternal ? null : uid}>{data.title}</Heading>
            {tags.length > 0 && (
                <TagSet>
                    {tags.map((tag, index) => (
                        <Tag key={index}>{tag}</Tag>
                    ))}
                </TagSet>
            )}
            <article className={styles.Definition}>
                <SliceZone components={SliceComponents} value={data.content.filter(isMedia)} fullscreen />
                <SliceZone components={SliceComponents} value={data.content.filter(isNotMedia)} />
                <Related linkToExternal={linkToExternal} items={data.related} />
            </article>
        </>
    )
}

Related.propTypes = {
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    linkToExternal: PropTypes.bool,
}

function Related({ items, linkToExternal }) {
    items = items.filter(isDefinition)

    return items.length === 0 ? null : (
        <div>
            <Muted>
                <FormattedMessage description="Layout glossary/layouts" defaultMessage="See also: " />
            </Muted>
            <ul>
                {items.filter(isNotBroken).map(({ definition }) => {
                    const { uid } = definition
                    const { title } = definition.data

                    return (
                        <li key={uid}>
                            {linkToExternal ? (
                                <Link href={'/glossary/terms/' + uid} title={title}>
                                    {title}
                                </Link>
                            ) : (
                                <a href={`#${uid}`} title={title}>
                                    {title}
                                </a>
                            )}
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

LetterTag.propTypes = {
    letter: PropTypes.string.isRequired,
}

function LetterTag({ letter }) {
    return (
        <Tag key={letter}>
            <a href={'#' + letter}>
                <b>{letter.toUpperCase()}</b>
            </a>
        </Tag>
    )
}

export function createSections(layout, definitions) {
    const definitionsByUID = definitions.reduce(
        (all, definition) => all.set(definition.uid, createDefinition(definition)),
        new Map()
    )
    const sections = Object.entries(layout).reduce((sections, [letter, definitions]) => {
        if (!LETTERS.has(letter) || definitions.filter(isDefinition).length === 0) {
            return sections
        }

        return sections.set(
            letter,
            definitions
                .filter(isDefinition)
                .map(({ definition }) => definitionsByUID.get(definition.uid))
                .filter(Boolean)
        )
    }, new Map())

    return Array.from(sections)
}

function GlossaryContent({ sections, term }) {
    const sectionsToRender = useMemo(() => {
        if (!term) {
            return new Map(sections)
        }

        const regexp = new RegExp(escapeRegExp(term), 'gi')
        function predicate(definition) {
            return regexp.test(definition.searchable)
        }

        return sections.reduce((filteredSections, [letter, definitions]) => {
            const filteredDefinitions = definitions.filter(predicate)

            if (filteredDefinitions.length > 0) {
                filteredSections.set(letter, filteredDefinitions)
            }

            return filteredSections
        }, new Map())
    }, [sections, term])

    if (sectionsToRender.size === 0) {
        return (
            <Muted>
                <FormattedMessage
                    description="Layout glossary/layouts"
                    defaultMessage="No definition matches your criteria."
                />
            </Muted>
        )
    }

    return (
        <>
            <TagSet>
                {Array.from(sectionsToRender.keys(), letter => (
                    <LetterTag key={letter} letter={letter} />
                ))}
            </TagSet>
            {Array.from(sectionsToRender.entries(), ([letter, definitions]) => (
                <Section key={letter} letter={letter} definitions={definitions} />
            ))}
        </>
    )
}

Section.propTypes = {
    letter: PropTypes.string.isRequired,
    definitions: PropTypes.array.isRequired,
}

function Section({ letter, definitions }) {
    return (
        <>
            <Heading as="h1" hash={letter}>
                {letter.toUpperCase()}
            </Heading>
            <section className={styles.Section}>
                {definitions.map(definition => (
                    <DefinitionLayout key={definition.uid} {...definition} />
                ))}
            </section>
        </>
    )
}

// Constants
const LETTERS = new Set('abcdefghijklmnopqrstuvwxyz')

// Utils
const isDefinition = ({ definition }) =>
    Boolean(definition) && definition.type === 'definition' && Boolean(definition.id)

const isNotBroken = ({ definition }) => definition.isBroken === false

const isText = ({ slice_type }) => slice_type === 'text'

const isMedia = ({ slice_type }) => slice_type === 'image' || slice_type === 'video'

const isNotMedia = props => !isMedia(props)

const createDefinition = definition => {
    const { data, tags } = definition
    const images = data.content.filter(isMedia)
    const related = data.related.filter(isNotBroken).filter(isDefinition)
    const texts = data.content.filter(isText)
    const searchables = [
        data.title,
        related.map(({ definition }) => definition.data.title),
        texts.map(({ primary }) => primary.content.map(({ text }) => text)),
        images.map(({ primary }) => [primary.credit, primary.caption.map(({ text }) => text)]),
        tags,
    ].flat(2)

    return {
        ...definition,
        searchable: searchables.filter(Boolean).join(' '),
    }
}
